import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ImageSlider from './ImageSlider';

/* ─────────────────────────────────────────────────────────────────
   ANIMATION VARIANTS
───────────────────────────────────────────────────────────────── */
const panelVariants = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit:    { opacity: 0, y: -18, transition: { duration: 0.22 } },
};
const fadeUp = (i = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.42, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
});

/* ─────────────────────────────────────────────────────────────────
   SHARED SUB-COMPONENTS
───────────────────────────────────────────────────────────────── */
function SLabel({ children }) {
  return (
    <p className="text-brand-orange text-[10px] font-bold tracking-[3px] uppercase mb-5 flex items-center gap-2">
      <span className="w-5 h-px bg-brand-orange inline-block" />{children}
    </p>
  );
}

function StatBadge({ value, label, icon, i }) {
  return (
    <motion.div {...fadeUp(i)} className="card card-hover rounded-2xl p-5 text-center flex flex-col items-center gap-1">
      {icon && <span className="text-2xl mb-1">{icon}</span>}
      <div className="font-heading font-black text-2xl sm:text-3xl text-gradient">{value}</div>
      <div className="text-brand-subtle text-xs leading-snug">{label}</div>
    </motion.div>
  );
}

function HeroBanner({ tag, headline, description }) {
  return (
    <div className="relative rounded-2xl overflow-hidden mb-8 bg-gradient-to-br from-gray-50 to-orange-50 border border-orange-100">
      <div className="relative z-10 px-8 py-10 sm:px-12 sm:py-12">
        <span className="section-label"><span className="w-5 h-px bg-brand-orange" />{tag}</span>
        <h3 className="font-heading font-black text-3xl sm:text-4xl lg:text-[2.75rem] text-brand-heading leading-tight mb-4 max-w-3xl">
          {headline}
        </h3>
        <p className="text-brand-subtle leading-relaxed max-w-2xl text-sm sm:text-base">{description}</p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="#contact" className="btn-primary">Request a Proposal ↗</a>
          <a href="#contact" className="btn-outline">Contact Us</a>
        </div>
      </div>
      <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gradient-to-br from-orange-100 to-orange-50 opacity-60 pointer-events-none" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   INSPECT PANEL
───────────────────────────────────────────────────────────────── */
const INSPECT = {
  tag: 'Third-Party Inspection',
  headline: 'Independent Inspection Services',
  description:
    'Eazyworld provides accredited, independent on-site inspection services that verify equipment condition, physical integrity, and statutory compliance — giving clients objective evidence before, during, and after every critical operation. Our inspectors operate free from contractor influence, ensuring findings are impartial, defensible, and legally recognised.',
  stats: [
    { value: '15+',  label: 'Years Experience' },
    { value: '500+', label: 'Assets Inspected' },
    { value: '100%', label: 'Statutory Coverage' },
    { value: '0',    label: 'Missed Defects' },
  ],
  services: [
    {
      icon: '🏗️', title: 'Lifts & Hoists', subtitle: 'Sec. 28/29 · Rule 55',
      items: [
        'Complete physical examination of condition & safeguards',
        'Interlocks, ropes/chains, brakes & limit switches',
        'Load testing & certification',
        'Statutory Form 8 / Form 9 issuance',
      ],
    },
    {
      icon: '⚙️', title: 'Lifting Machinery & Tackles', subtitle: 'Sec. 29 · Rule 55-A',
      items: [
        'Mobile, tower, EOT/gantry cranes',
        'Shackles, wire ropes, slings & lifting beams',
        'Safe working load verification',
        'Statutory Form 10 certification',
      ],
    },
    {
      icon: '🔥', title: 'Pressure Plant', subtitle: 'Sec. 31 · Rule 56 / 56-B',
      items: [
        'Shell/head integrity checks',
        'Relief devices & pressure gauges',
        'Ultrasonic thickness measurements',
        'Pressure tests & Form 11 certification',
      ],
    },
    {
      icon: '🦺', title: 'Safety Belts & Fall Protection', subtitle: 'Sec. 41 · Rule 61-A',
      items: [
        'Webbing, D-rings & buckle inspection',
        'Lanyards, energy absorbers & lifelines',
        'Traceability registers & tagging',
        'Re-inspection scheduling',
      ],
    },
  ],
  deliverables: [
    { icon: '📄', text: 'Statutory inspection certificates (Form 8 / 9 / 10 / 11)' },
    { icon: '📸', text: 'Detailed reports with photographic evidence' },
    { icon: '⚠️', text: 'Non-conformance registers with risk rankings' },
    { icon: '🗂️', text: 'Traceability records for all inspected assets' },
    { icon: '✅', text: 'Corrective action recommendations' },
  ],
};

function InspectPanel() {
  return (
    <motion.div key="inspect" variants={panelVariants} initial="initial" animate="animate" exit="exit">
      <HeroBanner tag={INSPECT.tag} headline={INSPECT.headline} description={INSPECT.description} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        {INSPECT.stats.map((s, i) => <StatBadge key={s.label} {...s} i={i} />)}
      </div>

      {/* Inspection Services */}
      <SLabel>Inspection Services</SLabel>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {INSPECT.services.map((svc, i) => (
          <motion.div key={svc.title} {...fadeUp(i)}
            className="group card card-hover rounded-2xl p-5 cursor-default flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center text-2xl mb-4 transition-colors duration-300 shrink-0">
              {svc.icon}
            </div>
            <h4 className="font-heading font-bold text-brand-heading text-sm mb-0.5 group-hover:text-brand-orange transition-colors duration-300">{svc.title}</h4>
            <p className="text-brand-orange text-[10px] font-bold tracking-wider uppercase mb-3">{svc.subtitle}</p>
            <ul className="space-y-2 mt-auto">
              {svc.items.map(item => (
                <li key={item} className="flex items-start gap-2 text-brand-subtle text-xs leading-relaxed">
                  <span className="text-brand-orange shrink-0 mt-0.5 text-[10px]">▸</span>{item}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      {/* Client Deliverables */}
      <div className="surface rounded-2xl p-7">
        <SLabel>Client Deliverables</SLabel>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INSPECT.deliverables.map((d, i) => (
            <motion.li key={d.text} {...fadeUp(i)}
              className="flex items-start gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm hover:border-orange-200 hover:shadow-md transition-all duration-300">
              <span className="text-xl shrink-0">{d.icon}</span>
              <span className="text-brand-subtle text-sm leading-relaxed">{d.text}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   AUDIT PANEL
───────────────────────────────────────────────────────────────── */
const AUDIT = {
  tag: 'EHS Audits & Compliance Assurance',
  headline: 'Turn Audit Findings into Measurable Improvement',
  description:
    "Eazyworld's structured EHS audit programmes move beyond simple checklists — they assess system performance, legal conformance, and control efficiency across your entire EHS management framework. Every audit delivers a clear gap analysis, a prioritised corrective action plan, and documented evidence of due diligence that protects your business.",
  stats: [
    { value: '5',    label: 'Audit Programmes' },
    { value: 'ISO',  label: '45001 / 14001 Aligned' },
    { value: '100%', label: 'Gap Visibility' },
    { value: '48h',  label: 'Report Turnaround' },
  ],
  programmes: [
    {
      icon: '📅', title: 'Monthly Project Audit',
      desc: 'Regular on-site EHS audits covering housekeeping, PPE compliance, permit-to-work systems, toolbox talk records, and operational controls. Keeps projects consistently compliant between milestone reviews.',
    },
    {
      icon: '📊', title: 'Quarterly Audit',
      desc: 'In-depth review of EHS management system performance, leading/lagging KPI trends, corrective action closure rates, and emerging risk patterns across the project or organisation.',
    },
    {
      icon: '🏆', title: 'Milestone / Pre-Handover Audit',
      desc: 'Comprehensive readiness assessment at critical project milestones or before site handover — verifying that all EHS obligations have been met and documented before client sign-off.',
    },
    {
      icon: '🤝', title: 'Contractor / Supplier Audit',
      desc: 'Pre-qualification and periodic audits of contractors and suppliers against project EHS requirements, including document reviews, site visits, and worker competence spot-checks.',
    },
    {
      icon: '🏅', title: 'ISO-Aligned Internal / Readiness Audit',
      desc: 'Gap analysis aligned to ISO 45001 (OHS) and ISO 14001 (Environmental) requirements. Prepares your organisation for external certification or surveillance audits with a prioritised closure plan.',
    },
  ],
  clientReceives: [
    { icon: '📑', text: 'Comprehensive audit reports with findings and ratings' },
    { icon: '🗺️', text: 'Prioritised corrective action plans (CAP) with owners & due dates' },
    { icon: '⚖️', text: 'Legal compliance register and gap matrix' },
    { icon: '📐', text: 'ISO readiness gap analysis reports' },
    { icon: '📈', text: 'Trend analysis and KPI dashboards' },
  ],
  keyValue: [
    { icon: '🎯', title: 'Objective View',       desc: 'Independent assessment free from internal bias or contractor influence.' },
    { icon: '🔻', title: 'Reduced Risk',         desc: 'Proactive gap closure before regulators or clients identify the same issues.' },
    { icon: '🛡️', title: 'Legal Defensibility', desc: 'Documented audit trails that demonstrate due diligence to all stakeholders.' },
    { icon: '📊', title: 'Stronger Governance',  desc: 'Prioritised roadmaps so resources go where they matter most.' },
  ],
};

function AuditPanel() {
  return (
    <motion.div key="audit" variants={panelVariants} initial="initial" animate="animate" exit="exit">
      <HeroBanner tag={AUDIT.tag} headline={AUDIT.headline} description={AUDIT.description} />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        {AUDIT.stats.map((s, i) => <StatBadge key={s.label} {...s} i={i} />)}
      </div>

      {/* Our Audit Programmes */}
      <SLabel>Our Audit Programmes</SLabel>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
        {AUDIT.programmes.map((prog, i) => (
          <motion.div key={prog.title} {...fadeUp(i)}
            className="group card card-hover rounded-2xl p-6 cursor-default flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center text-2xl mb-4 transition-colors duration-300 shrink-0">
              {prog.icon}
            </div>
            <h4 className="font-heading font-bold text-brand-heading text-sm mb-2 group-hover:text-brand-orange transition-colors duration-300">{prog.title}</h4>
            <p className="text-brand-subtle text-xs leading-relaxed">{prog.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* What Clients Receive + Key Value */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="surface rounded-2xl p-7">
          <SLabel>What Clients Receive</SLabel>
          <ul className="space-y-4">
            {AUDIT.clientReceives.map((item, i) => (
              <motion.li key={item.text} {...fadeUp(i)}
                className="flex items-start gap-3 text-brand-subtle text-sm leading-relaxed">
                <span className="text-xl shrink-0 mt-0.5">{item.icon}</span>
                {item.text}
              </motion.li>
            ))}
          </ul>
        </div>
        <div>
          <SLabel>Key Value</SLabel>
          <div className="grid sm:grid-cols-2 gap-4">
            {AUDIT.keyValue.map((kv, i) => (
              <motion.div key={kv.title} {...fadeUp(i)}
                className="flex gap-3 p-4 rounded-xl bg-gray-50 hover:bg-orange-50 border border-transparent hover:border-orange-100 transition-all duration-300 group">
                <div className="w-10 h-10 rounded-lg bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center text-lg shrink-0 transition-colors duration-300">
                  {kv.icon}
                </div>
                <div>
                  <div className="text-brand-heading font-semibold text-sm mb-0.5">{kv.title}</div>
                  <div className="text-brand-subtle text-xs leading-relaxed">{kv.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   TRAIN PANEL
───────────────────────────────────────────────────────────────── */
const TRAIN = {
  tag: 'Competence Training',
  headline: 'Competence That Moves From Classroom to Workfront',
  description:
    'Eazyworld builds lasting workforce competence through practical, accredited training programmes that address the root causes of incidents. Our training embeds a safety culture across all levels of an organisation — from equipment operators on the ground to engineers and safety teams in the office.',
  highlights: [
    { value: '46,471+', label: 'Participants Trained',  icon: '👥' },
    { value: '10,628+', label: 'Batches Delivered',     icon: '📚' },
    { value: '6',       label: 'Languages',             icon: '🌐' },
    { value: '100%',    label: 'Accredited Certificates', icon: '🏅' },
  ],
  trainingAreas: [
    {
      icon: '🏭', title: 'Industrial Training',
      desc: 'Core industrial safety and compliance training covering statutory requirements, permit-to-work systems, risk assessment, and industry-specific operations for all site personnel.',
    },
    {
      icon: '🚧', title: 'Construction Equipment Operators',
      desc: 'Safe operation training for mobile plant, cranes, lifts, and hoists — covering pre-start checks, load charts, exclusion zones, and emergency procedures.',
    },
    {
      icon: '⛓️', title: 'Riggers',
      desc: 'Practical rigging and slinging training covering load calculations, equipment inspection, sling selection, hand signals, and safe lifting operations aligned to statutory requirements.',
    },
    {
      icon: '👷', title: 'Supervisors',
      desc: 'Lifting supervisor and site supervisor programmes covering lift planning, risk assessment, permit-to-work systems, toolbox talks, incident reporting, and statutory responsibilities.',
    },
    {
      icon: '👔', title: 'Engineers & Safety Teams',
      desc: 'EHS officer development, incident investigation (RCA), ISO 45001/14001 implementation, legal register management, and leadership-level safety culture programmes.',
    },
  ],
  closedLoop: [
    { step: '01', title: 'Training Needs Analysis',   desc: 'Assess competence gaps and define learning objectives aligned to statutory and project requirements.' },
    { step: '02', title: 'Programme Design',           desc: 'Develop customised content, practical exercises, and assessments tailored to your workforce and equipment.' },
    { step: '03', title: 'Delivery & Assessment',      desc: 'Facilitate classroom, practical, and blended-learning sessions with competency-based assessments.' },
    { step: '04', title: 'Closed-Loop Improvement',    desc: 'Issue accredited certificates, update competence registers, and feed results back into the next training cycle.' },
  ],
};

function TrainPanel() {
  return (
    <motion.div key="train" variants={panelVariants} initial="initial" animate="animate" exit="exit">
      <HeroBanner tag={TRAIN.tag} headline={TRAIN.headline} description={TRAIN.description} />

      {/* Training Highlights */}
      <SLabel>Training Highlights</SLabel>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        {TRAIN.highlights.map((h, i) => <StatBadge key={h.label} {...h} i={i} />)}
      </div>

      {/* Training Areas */}
      <SLabel>Training Areas</SLabel>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
        {TRAIN.trainingAreas.map((area, i) => (
          <motion.div key={area.title} {...fadeUp(i)}
            className="group card card-hover rounded-2xl p-6 cursor-default flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center text-2xl mb-4 transition-colors duration-300 shrink-0">
              {area.icon}
            </div>
            <h4 className="font-heading font-bold text-brand-heading text-sm mb-2 group-hover:text-brand-orange transition-colors duration-300">{area.title}</h4>
            <p className="text-brand-subtle text-xs leading-relaxed">{area.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Closed-Loop Improvement */}
      <div className="surface rounded-2xl p-7">
        <SLabel>Closed-Loop Improvement</SLabel>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
          {TRAIN.closedLoop.map((step, i) => (
            <motion.div key={step.step} {...fadeUp(i)} className="relative flex sm:flex-col gap-4 sm:gap-3">
              {/* connector for desktop */}
              {i < TRAIN.closedLoop.length - 1 && (
                <div className="hidden lg:block absolute top-5 left-[calc(50%+1.6rem)] right-[-3rem] h-px bg-gradient-to-r from-brand-orange/40 to-transparent pointer-events-none" />
              )}
              {/* connector for mobile (vertical) */}
              {i < TRAIN.closedLoop.length - 1 && (
                <div className="sm:hidden absolute left-5 top-12 bottom-0 w-px bg-gradient-to-b from-brand-orange/30 to-transparent pointer-events-none" />
              )}
              <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-orange flex items-center justify-center font-heading font-black text-white text-xs z-10 sm:mx-auto">
                {step.step}
              </div>
              <div className="sm:text-center pb-6 sm:pb-0">
                <h4 className="font-heading font-bold text-brand-heading text-sm mb-1">{step.title}</h4>
                <p className="text-brand-subtle text-xs leading-relaxed">{step.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

const TABS = [
  {
    id: 'inspect',
    label: 'Inspect',
    icon: '🔍',
    tag: 'Third-Party Inspection',
    headline: ['Independent', 'Inspection', 'Services'],
    accent: 'Inspection',
    description:
      'Eazyworld provides independent, on-site inspection services that verify equipment condition, physical integrity, and statutory compliance — giving clients objective evidence before, during, and after critical operations.',
    stats: [
      { value: '15+', label: 'Years Experience' },
      { value: '500+', label: 'Assets Inspected' },
      { value: '100%', label: 'Statutory Coverage' },
      { value: '0', label: 'Missed Defects' },
    ],
    services: [
      { icon: '🏗️', title: 'Lifts, Hoists & Cranes', desc: 'Sec. 28/29, Rule 55 — complete examination of condition, safeguards, interlocks, ropes/chains, brakes, and limit switches.' },
      { icon: '⚙️', title: 'Lifting Machinery & Tackles', desc: 'Sec. 29, Rule 55-A — mobile, tower, EOT/gantry cranes, shackles, wire ropes, slings, lifting beams, and load tests.' },
      { icon: '🔥', title: 'Pressure Vessels & Plants', desc: 'Sec. 31, Rule 56/56-B — shell/head integrity, relief devices, gauges, ultrasonic thickness measurements, and pressure tests.' },
      { icon: '🦺', title: 'Fall Protection Systems', desc: 'Sec. 41, Rule 61-A — inspection of webbing, D-rings, buckles, lanyards, absorbers, lifelines, and traceability registers.' },
      { icon: '📐', title: 'Ultrasonic Thickness Testing', desc: 'Non-destructive UT measurements on pressure vessels, pipework, and structural members to detect wall-loss and corrosion.' },
      { icon: '🔩', title: 'Pre-Mobilisation Checks', desc: 'Equipment readiness verification before site deployment — ensuring assets meet statutory and project-specific requirements.' },
    ],
    process: [
      { num: '01', title: 'Scope Confirmation', desc: 'Define statutory rules, asset register, and target inspection dates.' },
      { num: '02', title: 'On-Site Inspection', desc: 'Deploy certified inspectors for physical examination, load testing, and UT measurements.' },
      { num: '03', title: 'Defect Escalation', desc: 'Critical findings escalated immediately; non-conformances risk-ranked for priority closure.' },
      { num: '04', title: 'Certification & Report', desc: 'Deliver Form 8/9/10/11 certificates, traceable records, and compliance recommendations.' },
    ],
    features: [
      'Lifting equipment & cranes (Sec. 28/29, Rule 55)',
      'Pressure vessels & plants (Sec. 31, Rule 56)',
      'Fall protection systems (Sec. 41, Rule 61-A)',
      'Ultrasonic thickness & load testing',
      'Pre-mobilisation & in-service inspections',
      'Statutory Form 8/9/10/11 certification',
    ],
    deliverables: [
      'Statutory inspection certificates (Form 8/9/10/11)',
      'Detailed inspection reports with photographic evidence',
      'Non-conformance registers with risk rankings',
      'Traceability records for all inspected assets',
      'Corrective action recommendations',
    ],
    benefits: [
      { icon: '📄', title: 'Traceable Records', desc: 'Full audit trail of inspection certificates and evidence for every asset.' },
      { icon: '⚠️', title: 'Early Defect Detection', desc: 'Identify failure risks before they become incidents or costly breakdowns.' },
      { icon: '⚖️', title: 'Legal Defensibility', desc: 'Regulatory compliance backed by independent, certified documentation.' },
      { icon: '🕐', title: 'Reduced Downtime', desc: 'Planned interventions prevent unplanned shutdowns and project delays.' },
    ],
  },
  {
    id: 'audit',
    label: 'Audit',
    icon: '📋',
    tag: 'EHS Compliance Auditing',
    headline: ['EHS Compliance', 'Auditing &', 'Assurance'],
    accent: 'Auditing',
    description:
      "Eazyworld's audit services assess system performance, legal conformance, and control efficiency across your EHS management framework — delivering clear gap analyses and prioritised corrective action plans.",
    stats: [
      { value: '3', label: 'Audit Frequencies' },
      { value: 'ISO', label: '45001 / 14001 Aligned' },
      { value: '100%', label: 'Gap Visibility' },
      { value: '48h', label: 'Report Turnaround' },
    ],
    services: [
      { icon: '📅', title: 'Monthly Site Audits', desc: 'Regular on-site EHS audits covering housekeeping, PPE compliance, permit systems, and operational controls.' },
      { icon: '📊', title: 'Quarterly System Evaluations', desc: 'In-depth review of EHS management system performance, KPI trends, and corrective action closure rates.' },
      { icon: '🏆', title: 'ISO 45001 Readiness Reviews', desc: 'Gap analysis against ISO 45001 requirements to prepare for certification or surveillance audits.' },
      { icon: '🌿', title: 'ISO 14001 Environmental Audits', desc: 'Environmental management system reviews covering waste, emissions, spill controls, and legal compliance.' },
      { icon: '📜', title: 'Legal Register & Gap Analysis', desc: 'Identification of applicable legislation, assessment of current compliance status, and gap closure planning.' },
      { icon: '🤝', title: 'Contractor & Supply-Chain Audits', desc: 'Pre-qualification and periodic audits of contractors and suppliers against project EHS requirements.' },
    ],
    process: [
      { num: '01', title: 'Audit Planning', desc: 'Define scope, applicable legislation, ISO standards, and audit schedule aligned to project milestones.' },
      { num: '02', title: 'On-Site Assessment', desc: 'Physical site walkthrough, document review, personnel interviews, and observation of work activities.' },
      { num: '03', title: 'Gap Analysis', desc: 'Identify non-conformances, legal gaps, and system weaknesses with risk-based prioritisation.' },
      { num: '04', title: 'Corrective Action Plan', desc: 'Deliver prioritised CAP with responsible parties, target dates, and verification criteria.' },
    ],
    features: [
      'Monthly, quarterly & annual EHS audits',
      'ISO 45001 / ISO 14001 readiness reviews',
      'Legal register & regulatory gap analysis',
      'Management system documentation review',
      'Contractor and supply-chain EHS audits',
      'Pre-handover milestone readiness assessments',
    ],
    deliverables: [
      'Comprehensive audit reports with findings and ratings',
      'Prioritised corrective action plans (CAP)',
      'Legal compliance register and gap matrix',
      'ISO readiness gap analysis reports',
      'Trend analysis and KPI dashboards',
    ],
    benefits: [
      { icon: '🎯', title: 'Objective Compliance View', desc: 'Independent assessment free from internal bias or contractor influence.' },
      { icon: '🗺️', title: 'Prioritised Roadmaps', desc: 'Risk-ranked corrective actions so resources go where they matter most.' },
      { icon: '🛡️', title: 'Reduced Regulatory Risk', desc: 'Proactive gap closure before regulators or clients identify the same issues.' },
      { icon: '📈', title: 'Stronger Governance', desc: 'Documented audit trails that demonstrate due diligence to all stakeholders.' },
    ],
  },
  {
    id: 'train',
    label: 'Train',
    icon: '🎓',
    tag: 'Competence Development',
    headline: ['Workforce', 'Competence', 'Development'],
    accent: 'Competence',
    description:
      "Eazyworld builds lasting workforce competence through practical, accredited training programmes that address the root causes of incidents and embed a culture of safety across all levels of your organisation.",
    stats: [
      { value: '15+', label: 'Years Training Delivery' },
      { value: '20+', label: 'Course Programmes' },
      { value: '1000+', label: 'Personnel Trained' },
      { value: '100%', label: 'Accredited Certificates' },
    ],
    services: [
      { icon: '🏗️', title: 'Rigger & Banksman Courses', desc: 'Practical rigging, slinging, and signalling training covering load calculations, equipment inspection, and safe lifting operations.' },
      { icon: '👷', title: 'Lifting Supervisor Programme', desc: 'Comprehensive lifting supervisor training covering lift planning, risk assessment, permit systems, and statutory responsibilities.' },
      { icon: '🦺', title: 'Working-at-Height Training', desc: 'Fall protection, harness inspection, rescue planning, and safe work at height procedures for all personnel.' },
      { icon: '📋', title: 'EHS Officer Programme', desc: 'Full EHS officer development covering legislation, risk assessment, incident investigation, and management system implementation.' },
      { icon: '🔍', title: 'Incident Investigation & RCA', desc: 'Root cause analysis methodologies, investigation techniques, and corrective action development for EHS professionals.' },
      { icon: '🎯', title: 'Customised On-Site Delivery', desc: 'Bespoke training programmes designed around your specific equipment, processes, and workforce competence gaps.' },
    ],
    process: [
      { num: '01', title: 'Training Needs Analysis', desc: 'Assess current competence levels, identify gaps, and define learning objectives aligned to statutory requirements.' },
      { num: '02', title: 'Programme Design', desc: 'Develop customised content, practical exercises, and assessments tailored to your workforce and equipment.' },
      { num: '03', title: 'Delivery & Assessment', desc: 'Facilitate classroom, practical, and blended-learning sessions with competency-based assessments.' },
      { num: '04', title: 'Certification & Records', desc: 'Issue accredited certificates, update competence registers, and provide refresher scheduling recommendations.' },
    ],
    features: [
      'Rigger, banksman & lifting supervisor courses',
      'Working-at-height & fall-protection training',
      'EHS officer & safety representative programmes',
      'Incident investigation & root-cause analysis',
      'Customised on-site and blended-learning delivery',
      'Competence register management and tracking',
    ],
    deliverables: [
      'Accredited training certificates per participant',
      'Competence assessment records and registers',
      'Training needs analysis reports',
      'Customised course materials and handbooks',
      'Refresher and re-certification schedules',
    ],
    benefits: [
      { icon: '📈', title: 'Measurable Competence Gains', desc: 'Pre/post assessments demonstrate real improvement in workforce knowledge and skills.' },
      { icon: '🔻', title: 'Fewer Incidents', desc: 'Competent workers make better decisions — reducing recurring incidents and near-misses.' },
      { icon: '🏅', title: 'Recognised Certificates', desc: 'Accredited certificates accepted by regulators, clients, and project owners.' },
      { icon: '🔄', title: 'Scalable Programmes', desc: 'From single operators to large project teams — delivery scales to your workforce size.' },
    ],
  },
];

const tabPanelVariants = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit:    { opacity: 0, y: -16, transition: { duration: 0.2 } },
};

const stagger = (i) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
});

function SectionLabel({ children }) {
  return (
    <p className="text-brand-orange text-[10px] font-bold tracking-[3px] uppercase mb-4 flex items-center gap-2">
      <span className="w-5 h-px bg-brand-orange" />{children}
    </p>
  );
}

function ServiceCard({ icon, title, desc, index }) {
  return (
    <motion.div
      {...stagger(index)}
      className="group card card-hover rounded-2xl p-5 cursor-default"
    >
      <div className="w-11 h-11 rounded-xl bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center text-xl mb-4 transition-colors duration-300">
        {icon}
      </div>
      <h4 className="font-heading font-bold text-brand-heading text-sm mb-2 group-hover:text-brand-orange transition-colors duration-300">{title}</h4>
      <p className="text-brand-subtle text-xs leading-relaxed">{desc}</p>
    </motion.div>
  );
}

function ProcessStep({ num, title, desc, index, total }) {
  return (
    <div className="relative flex gap-4">
      {index < total - 1 && (
        <div className="absolute left-5 top-12 bottom-0 w-px bg-gradient-to-b from-brand-orange/30 to-transparent" />
      )}
      <div className="shrink-0 w-10 h-10 rounded-xl bg-brand-orange flex items-center justify-center font-heading font-black text-white text-xs z-10">
        {num}
      </div>
      <div className="pb-8">
        <h4 className="font-heading font-bold text-brand-heading text-sm mb-1">{title}</h4>
        <p className="text-brand-subtle text-xs leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function BenefitCard({ icon, title, desc, index }) {
  return (
    <motion.div
      {...stagger(index)}
      className="flex gap-4 p-4 rounded-xl bg-gray-50 hover:bg-orange-50 border border-transparent hover:border-orange-100 transition-all duration-300 group"
    >
      <div className="w-10 h-10 rounded-lg bg-orange-100 group-hover:bg-orange-200 flex items-center justify-center text-lg shrink-0 transition-colors duration-300">
        {icon}
      </div>
      <div>
        <div className="text-brand-heading font-semibold text-sm mb-0.5">{title}</div>
        <div className="text-brand-subtle text-xs leading-relaxed">{desc}</div>
      </div>
    </motion.div>
  );
}

function TabPanel({ tab }) {
  return (
    <motion.div key={tab.id} variants={tabPanelVariants} initial="initial" animate="animate" exit="exit">

      {/* Hero strip */}
      <div className="relative rounded-2xl overflow-hidden mb-8 bg-gradient-to-br from-gray-50 to-orange-50 border border-orange-100">
        <div className="relative z-10 px-8 py-10 sm:px-12 sm:py-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="section-label">
              <span className="w-5 h-px bg-brand-orange" />{tab.tag}
            </span>
            <h3 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-heading leading-tight mb-5">
              {tab.headline.map((line, i) => (
                <span key={i} className={line === tab.accent ? 'text-gradient' : ''}>
                  {line}{i < tab.headline.length - 1 ? <br /> : ''}
                </span>
              ))}
            </h3>
            <p className="text-brand-subtle leading-relaxed max-w-2xl text-sm sm:text-base">{tab.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#contact" className="btn-primary">Request a Proposal ↗</a>
              <a href="#contact" className="btn-outline">Contact Us</a>
            </div>
          </div>
          <div className="hidden lg:block rounded-xl overflow-hidden border border-gray-200 shadow-card">
            <video src="/videos/vidno2.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover" />
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {tab.stats.map((s, i) => (
          <motion.div key={s.label} {...stagger(i)} className="card rounded-2xl p-5 text-center card-hover">
            <div className="font-heading font-black text-2xl sm:text-3xl text-gradient mb-1">{s.value}</div>
            <div className="text-brand-subtle text-xs">{s.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Services + Process */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <SectionLabel>Services Covered</SectionLabel>
          <div className="grid sm:grid-cols-2 gap-4">
            {tab.services.map((s, i) => (
              <ServiceCard key={s.title} {...s} index={i} />
            ))}
          </div>
        </div>

        <div className="surface rounded-2xl p-6 h-fit">
          <SectionLabel>How It Works</SectionLabel>
          {tab.process.map((step, i) => (
            <ProcessStep key={step.num} {...step} index={i} total={tab.process.length} />
          ))}
        </div>
      </div>

      {/* Features + Deliverables + Benefits */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="surface rounded-2xl p-6">
          <SectionLabel>Key Features</SectionLabel>
          <ul className="space-y-3">
            {tab.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-brand-subtle text-sm">
                <span className="text-brand-orange mt-0.5 shrink-0 text-xs">▸</span>{f}
              </li>
            ))}
          </ul>
        </div>

        <div className="surface rounded-2xl p-6">
          <SectionLabel>Client Deliverables</SectionLabel>
          <ul className="space-y-3">
            {tab.deliverables.map((d) => (
              <li key={d} className="flex items-start gap-3 text-brand-subtle text-sm">
                <span className="text-green-500 mt-0.5 shrink-0 text-xs">✓</span>{d}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <SectionLabel>Benefits</SectionLabel>
          <div className="space-y-3">
            {tab.benefits.map((b, i) => (
              <BenefitCard key={b.title} {...b} index={i} />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function About() {
  const [active, setActive] = useState('inspect');
  const tab = TABS.find(t => t.id === active);

  return (
    <section id="about" className="section-pad bg-white relative overflow-hidden">
      <div className="container-xl relative z-10">

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="mb-4"
        >
          <span className="section-label">
            <span className="w-8 h-px bg-brand-orange" /> Company Profile
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12"
        >
          <h2 className="font-heading font-black text-4xl sm:text-5xl text-brand-heading leading-tight">
            INTEGRATED <span className="text-gradient">ASSURANCE</span><br />FOR TODAY'S COMPLIANCE
          </h2>
          <p className="text-brand-subtle text-sm leading-relaxed max-w-sm sm:text-right">
            15 years of engineering and EHS expertise — delivered with independence, transparency, and integrity.
          </p>
        </motion.div>

        {/* Tab navigation */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
          className="flex gap-2 p-2 rounded-2xl w-full sm:w-fit mb-10"
          style={{ background: '#F3F4F6', border: '1px solid #E5E7EB' }}
        >
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              style={
                active === t.id
                  ? {
                      background: 'linear-gradient(135deg, #E8500A 0%, #FF6A00 100%)',
                      boxShadow: '0 4px 14px rgba(232,80,10,0.45)',
                      color: '#ffffff',
                      border: 'none',
                    }
                  : {
                      background: 'transparent',
                      color: '#6B7280',
                      border: 'none',
                    }
              }
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-7 py-3 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <span className="text-base">{t.icon}</span>
              <span>{t.label}</span>
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <TabPanel key={active} tab={tab} />
        </AnimatePresence>

        <div className="mt-12">
          <ImageSlider page="about" />
        </div>
      </div>
    </section>
  );
}
